import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, TrendingUp, FileSpreadsheet, Printer, Bell, Mail, X, AlertCircle } from 'lucide-react';
import { approvalService, CollectiveAssessmentData, eventCycleService, CycleEvent, AssessmentDraftItem, submissionService, Category, ExportFormat } from '../services/adminAuth';
import Swal from 'sweetalert2';
import XLSX from 'xlsx-js-style';
import { useNavigate } from 'react-router-dom';
// import ExportButton from '../../sharedComponents/ExportButton';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function CollectiveAssessment() {
    const [passMarkThreshold, setPassMarkThreshold] = useState<number>(0);
    const [applicationType, setApplicationType] = useState<number>(0); // 0 for unselected, 1 for Books, 2 for Journal
    const [categoryId, setCategoryId] = useState<number>(0);
    const [categories, setCategories] = useState<Category[]>([]);
    const [stats, setStats] = useState<CollectiveAssessmentData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [draftMode, setDraftMode] = useState<'passed' | 'failed'>('passed');
    const [activeCycle, setActiveCycle] = useState<CycleEvent | null>(null);
    const navigate = useNavigate();

    const fetchAssessmentData = async (threshold: number, type: number, catId: number = 0) => {
        setIsLoading(true);
        try {
            const response = await approvalService.getCollectiveAssessmentCount({
                averageScore: threshold,
                applicationType: 0, // Force 0 as requested
                thematicAreaId: catId,
            });
            if (response.isSuccess && response.data) {
                setStats(response.data);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.message || 'Failed to fetch assessment data'
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An unexpected error occurred while fetching assessment data'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchActiveCycle = async () => {
        try {
            const response = await eventCycleService.getActiveCycleEvent();
            if (response.isSuccess && response.data) {
                setActiveCycle(response.data);
            }
        } catch (error) {
            }
    };

    const fetchCategories = async () => {
        try {
            const response = await submissionService.getAllCategories(true);
            if (response.isSuccess && response.data) {
                setCategories(response.data);
            }
        } catch (error) {
            }
    };

    useEffect(() => {
        fetchActiveCycle();
        fetchCategories();
    }, []);

    const passed = stats?.passed || 0;
    const failed = stats?.failed || 0;
    const totalAssessed = stats?.total || 0;
    const successRate = totalAssessed > 0 ? Math.round((passed / totalAssessed) * 100) : 0;

    const handleRecalculate = () => {
        fetchAssessmentData(passMarkThreshold, applicationType, categoryId);
    };

    const handleApprove = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to approve assessments based on a ${passMarkThreshold}% pass mark.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#15803D',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Approve',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const response = await approvalService.makeApprovalDecision(passMarkThreshold, 0, categoryId); // Force 0
                    if (!response.isSuccess) {
                        throw new Error(response.message || 'Approval failed');
                    }
                    return response;
                } catch (error: any) {
                    Swal.showValidationMessage(`Request failed: ${error.message}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });

        if (result.isConfirmed) {
            Swal.fire({
                icon: 'success',
                title: 'Approved!',
                text: 'The assessment decisions have been finalized.',
                confirmButtonColor: '#15803D',
                showCancelButton: true,
                confirmButtonText: 'View Approved List',
                cancelButtonText: 'Close'
            }).then((res) => {
                if (res.isConfirmed) {
                    navigate('/admin/final-assessment');
                }
            });
        }
    };

    const generateDraftExcelFile = (data: AssessmentDraftItem[], reportTitle: string) => {
        const headers = [
            'S/N', 'Author Name', 'Sub. No', 'Title', 'Type', 'Thematic Area', 'Avg Score', 'Geo-Political Zone', 'Status', 'Reviewer 1', 'Score 1', 'Remarks 1', 'Reviewer 2', 'Score 2', 'Remarks 2',
        ];

        const bodyData = data.map((item, index) => {
            const r1 = item.reviewers?.[0];
            const r2 = item.reviewers?.[1];
            return [
                index + 1,
                item.fullName || 'N/A',
                item.submissionNumber || 'N/A',
                item.title || 'N/A',
                applicationType === 1 ? 'Books' : 'Journal',
                item.categoryName || 'N/A',
                item.averageScore?.toFixed(2) || '0.00',
                item.geoPoliticalZone || 'N/A',
                item.status || 'N/A',
                r1?.reviewerName || 'N/A',
                r1?.review?.score ?? 'N/A',
                r1?.review?.remarks || 'N/A',
                r2?.reviewerName || 'N/A',
                r2?.review?.score ?? 'N/A',
                r2?.review?.remarks || 'N/A',
            ];
        });

        const wsData = [
            ['TECHNICAL ADVISORY GROUP (TAG)'],
            [reportTitle],
            [activeCycle?.cycle?.name || 'Current Cycle'],
            [`Pass Mark Threshold: ${passMarkThreshold}%`],
            [],
            headers,
            ...bodyData
        ];

        const worksheet = XLSX.utils.aoa_to_sheet(wsData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Draft List');

        XLSX.writeFile(workbook, `${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const generateDraftPDFFile = (data: AssessmentDraftItem[], reportTitle: string) => {
        const doc = new jsPDF('landscape');
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('TECHNICAL ADVISORY GROUP (TAG)', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(12);
        doc.text(reportTitle, pageWidth / 2, 22, { align: 'center' });

        const headers = [[
            'S/N', 'Author', 'Sub. No', 'Title', 'Avg Score', 'Status', 'Reviewer 1', 'Score 1', 'Reviewer 2', 'Score 2'
        ]];

        const bodyData = data.map((item, index) => [
            index + 1,
            item.fullName || '—',
            item.submissionNumber || '—',
            item.title || '—',
            item.averageScore?.toFixed(2) || '0.00',
            item.status || '—',
            item.reviewers?.[0]?.reviewerName || '—',
            item.reviewers?.[0]?.review?.score ?? '—',
            item.reviewers?.[1]?.reviewerName || '—',
            item.reviewers?.[1]?.review?.score ?? '—'
        ]);

        autoTable(doc, {
            head: headers,
            body: bodyData,
            startY: 30,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [21, 128, 61], textColor: 255 },
            margin: { top: 35 }
        });

        doc.save(`${reportTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const draftReportParams = (pullFailSubmissions: boolean) => ({
        averageScore: passMarkThreshold,
        getBelowAverage: pullFailSubmissions,
        thematicAreaId: categoryId,
        applicationType: 0, // Force 0
    });

    interface StatCard {
        title: string;
        value: string;
        icon: React.ReactNode;
        iconBg: string;
        textColor: string;
        cardBg: string;
        borderColor: string;
    }

    const statsData: StatCard[] = [
        {
            title: 'Total Assessed',
            value: totalAssessed.toString(),
            icon: <Users className="w-6 h-6 text-[#155DFC]" />,
            iconBg: 'bg-[#EFF6FF]',
            textColor: 'text-gray-900',
            cardBg: 'bg-white',
            borderColor: 'border-[#E2E8F0]'
        },
        {
            title: 'Passed',
            value: passed.toString(),
            icon: <CheckCircle className="w-6 h-6 text-[#00A63E]" />,
            iconBg: 'bg-[#DCFCE7]',
            textColor: 'text-[#00A63E]',
            cardBg: 'bg-[#F0FDF44D]',
            borderColor: 'border-[#B9F8CF]'
        },
        {
            title: 'Failed',
            value: failed.toString(),
            icon: <XCircle className="w-6 h-6 text-[#E7000B]" />,
            iconBg: 'bg-[#FFE2E2]',
            textColor: 'text-[#E7000B]',
            cardBg: 'bg-[#FEF2F24D]',
            borderColor: 'border-[#FFC9C9]'
        },
        {
            title: 'Success Rate',
            value: `${successRate}%`,
            icon: <TrendingUp className="w-6 h-6 text-[#9810FA]" />,
            iconBg: 'bg-[#F3E8FF]',
            textColor: 'text-[#9810FA]',
            cardBg: 'bg-[#FAF5FF4D]',
            borderColor: 'border-[#E9D4FF]'
        }
    ];

    return (
        <div className="w-full">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Collective Assessment Results</h1>
                <p className="text-gray-500">Review aggregated scores and finalize decisions for {activeCycle?.cycle?.name || 'Loading...'}.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {statsData.map((stat, index) => (
                    <div key={index} className={`bg-white shadow-sm rounded-lg border px-6 py-4 ${stat.cardBg} ${stat.borderColor}`}>
                        <div className="flex items-center justify-between">
                            <div className={`${stat.textColor}`}>
                                <span className="text-sm font-medium text-gray-500">{stat.title}</span>
                                <div className="text-3xl font-bold mt-2">{stat.value}</div>
                            </div>
                            <div className={`w-12 h-12 rounded-full ${stat.iconBg} flex items-center justify-center`}>
                                {stat.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-1">Recalculate Results</h3>
                            <p className="text-sm text-gray-500">Set criteria to evaluate submissions</p>
                        </div>

                        <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Threshold Score (%)</label>
                                    <input
                                        type="number"
                                        value={passMarkThreshold === 0 ? '' : passMarkThreshold}
                                        onChange={(e) => setPassMarkThreshold(e.target.value === '' ? 0 : Number(e.target.value))}
                                        placeholder="0"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none"
                                        min="0" max="100"
                                    />
                                </div>
                                {/* <div>
                                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Application Type</label>
                                    <select
                                        value={applicationType}
                                        onChange={(e) => setApplicationType(Number(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none"
                                    >
                                        <option value={0} disabled>Select Application Type</option>
                                        <option value={1}>Books</option>
                                        <option value={2}>Journal</option>
                                    </select>
                                </div> */}
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">Thematic Area</label>
                                    <select
                                        value={categoryId}
                                        onChange={(e) => setCategoryId(Number(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 font-medium focus:ring-2 focus:ring-green-500 outline-none"
                                    >
                                        <option value={0}>All Thematic Areas</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    onClick={handleRecalculate}
                                    disabled={isLoading}
                                    className="px-6 py-2 bg-[#15803D] text-white rounded-lg font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? 'Recalculating...' : 'Recalculate'}
                                </button>
                            </div>
                        </div>

                        {stats && (
                            <div className="mt-8 space-y-8 animate-in fade-in duration-500">
                                <div>
                                    <div className="flex items-center justify-between mb-3 text-sm font-medium">
                                        <span className="text-gray-900">Pass/Fail Distribution</span>
                                        <span className="text-gray-500">{totalAssessed} submissions</span>
                                    </div>
                                    <div className="w-full h-8 flex rounded-md overflow-hidden bg-gray-100 shadow-inner">
                                        {totalAssessed > 0 ? (
                                            <>
                                                <div
                                                    className="bg-green-500 flex items-center justify-center text-white text-xs font-bold transition-all duration-500"
                                                    style={{ width: `${(passed / totalAssessed) * 100}%` }}
                                                >
                                                    {passed > 0 && `${passed} Passed`}
                                                </div>
                                                <div
                                                    className="bg-red-500 flex items-center justify-center text-white text-xs font-bold transition-all duration-500"
                                                    style={{ width: `${(failed / totalAssessed) * 100}%` }}
                                                >
                                                    {failed > 0 && `${failed} Failed`}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="w-full flex items-center justify-center text-gray-400 text-xs">No data available</div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Thematic Area Distribution</h4>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-gray-50 text-gray-600 font-medium">
                                                <tr>
                                                    <th className="px-4 py-2 border-b">Thematic Area</th>
                                                    <th className="px-4 py-2 border-b text-center">Passed</th>
                                                    <th className="px-4 py-2 border-b text-center">Failed</th>
                                                    <th className="px-4 py-2 border-b text-center">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {Object.keys({ ...stats.passedAssessmentDistribution, ...stats.failedAssessmentDistribution }).map((cat) => (
                                                    <tr key={cat} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 font-medium text-gray-700">{cat}</td>
                                                        <td className="px-4 py-3 text-center text-green-600 font-bold">{stats.passedAssessmentDistribution[cat] || 0}</td>
                                                        <td className="px-4 py-3 text-center text-red-600 font-bold">{stats.failedAssessmentDistribution[cat] || 0}</td>
                                                        <td className="px-4 py-3 text-center text-gray-900 bg-gray-50/50">
                                                            {(stats.passedAssessmentDistribution[cat] || 0) + (stats.failedAssessmentDistribution[cat] || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {Object.keys({ ...stats.passedAssessmentDistribution, ...stats.failedAssessmentDistribution }).length === 0 && (
                                                    <tr>
                                                        <td colSpan={4} className="px-4 py-8 text-center text-gray-400 italic">No category data found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="pt-4">
                                    {/* <ExportButton
                                        reportType={5}
                                        parameters={draftReportParams(draftMode === 'failed')}
                                        reportTitle="Assessment Draft"
                                        buttonText={`${draftMode === 'failed' ? 'Export Failed' : 'Export Passed'} Draft`}
                                        disabled={isExporting}
                                        className="w-full md:w-auto border border-gray-300 bg-white text-gray-900 hover:bg-gray-50 shadow-sm"
                                    /> */}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-1">Approve Decision</h3>
                            <p className="text-sm text-gray-500">Finalize assessments based on the current threshold.</p>
                        </div>
                        <div className="space-y-3">
                            <button
                                onClick={handleApprove}
                                disabled={totalAssessed === 0}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#15803D] text-white rounded-lg font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle className="w-5 h-5" />
                                Approve Decision
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-900 mb-1">Final Assessments</h3>
                            <p className="text-sm text-gray-500">View approved and rejected lists</p>
                        </div>
                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/admin/final-assessment')}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                            >
                                <FileSpreadsheet className="w-5 h-5 text-gray-500" />
                                View Final Assessment Lists
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CollectiveAssessment;